import * as THREE from 'three';

/**
 * Surface detail without textures: world-space noise layered into the
 * standard material so walls, roofs and the street stop looking like clean
 * plastic. Grime collects near the ground, rain streaks run down walls, and
 * the road gets rough patches and (on wet nights) mirror-smooth puddles.
 */

const NOISE = /* glsl */ `
  varying vec3 vSurfWorld;
  float sHash(vec3 p) {
    p = fract(p * 0.3183099 + 0.1);
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  float sNoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(sHash(i), sHash(i + vec3(1, 0, 0)), f.x), mix(sHash(i + vec3(0, 1, 0)), sHash(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(sHash(i + vec3(0, 0, 1)), sHash(i + vec3(1, 0, 1)), f.x), mix(sHash(i + vec3(0, 1, 1)), sHash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
  }
  float sFbm(vec3 p) {
    return sNoise(p) * 0.55 + sNoise(p * 2.13) * 0.3 + sNoise(p * 4.7) * 0.15;
  }
`;

export type SurfaceKind = 'wall' | 'roof' | 'ground';

export function addSurfaceDetail(material: THREE.MeshStandardMaterial, kind: SurfaceKind, wet = false): void {
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSurfWorld;')
      .replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
        vSurfWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
        #ifdef USE_INSTANCING
          vSurfWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
        #endif`,
      );
    let colour = '';
    let rough = '';
    if (kind === 'wall') {
      colour = `
        float patchy = sFbm(vSurfWorld * 0.45);
        float streak = sFbm(vec3(vSurfWorld.x * 2.2, vSurfWorld.y * 0.12, vSurfWorld.z * 2.2));
        float grime = smoothstep(1.8, 0.0, vSurfWorld.y) * 0.35;
        float dirt = clamp(grime + smoothstep(0.45, 0.9, streak) * 0.3 + (patchy - 0.5) * 0.25, -0.15, 0.6);
        diffuseColor.rgb *= 1.0 - dirt;`;
      rough = `roughnessFactor = clamp(roughnessFactor - smoothstep(0.6, 0.95, streak) * 0.25, 0.04, 1.0);`;
    } else if (kind === 'roof') {
      colour = `
        float patchy = sFbm(vSurfWorld * 0.8);
        diffuseColor.rgb *= 0.85 + patchy * 0.3;`;
      rough = `roughnessFactor = clamp(roughnessFactor + (patchy - 0.5) * 0.3, 0.04, 1.0);`;
    } else {
      colour = `
        float patchy = sFbm(vSurfWorld * vec3(0.35, 0.0, 0.35) + 3.1);
        float fine = sNoise(vSurfWorld * vec3(3.0, 0.0, 3.0));
        diffuseColor.rgb *= 0.86 + patchy * 0.22 + (fine - 0.5) * 0.08;`;
      rough = wet
        ? `float puddle = smoothstep(0.58, 0.66, sFbm(vSurfWorld * vec3(0.22, 0.0, 0.22) + 9.7));
           roughnessFactor = mix(clamp(roughnessFactor + (patchy - 0.5) * 0.3, 0.04, 1.0), 0.03, puddle);
           diffuseColor.rgb *= 1.0 - puddle * 0.35;`
        : `roughnessFactor = clamp(roughnessFactor + (patchy - 0.5) * 0.2, 0.04, 1.0);`;
    }
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${NOISE}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${colour}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${rough}`);
  };
  material.customProgramCacheKey = () => `surface-${kind}-${wet ? 1 : 0}`;
  material.needsUpdate = true;
}

/**
 * A dim night-city environment for reflections: a dark sky, a faint horizon
 * glow, and scattered warm and cool lights. Wet streets, glass and metal pick
 * these up as soft highlights; the diffuse lift stays tiny.
 */
export function createNightEnvironment(renderer: THREE.WebGLRenderer): THREE.Texture {
  const env = new THREE.Scene();
  env.background = new THREE.Color('#030407');
  const sphere = new THREE.SphereGeometry(1, 12, 8);
  const disposables: { dispose(): void }[] = [sphere];
  const glow = (color: string, strength: number) => {
    const m = new THREE.MeshBasicMaterial({ color });
    m.color.multiplyScalar(strength);
    disposables.push(m);
    return m;
  };
  // Horizon band of distant city light.
  const band = new THREE.Mesh(new THREE.CylinderGeometry(40, 40, 6, 32, 1, true), glow('#2a2430', 1));
  (band.material as THREE.MeshBasicMaterial).side = THREE.BackSide;
  band.position.y = -1;
  disposables.push(band.geometry);
  env.add(band);
  let seed = 7;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const warm = glow('#ffcf8a', 6);
  const cool = glow('#cfe2ff', 5);
  for (let i = 0; i < 40; i += 1) {
    const a = rand() * Math.PI * 2;
    const y = -2 + rand() * 12;
    const m = new THREE.Mesh(sphere, rand() < 0.7 ? warm : cool);
    m.position.set(Math.cos(a) * 30, y, Math.sin(a) * 30);
    m.scale.setScalar(0.4 + rand() * 0.8);
    env.add(m);
  }
  const pmrem = new THREE.PMREMGenerator(renderer);
  const target = pmrem.fromScene(env, 0.02);
  pmrem.dispose();
  for (const d of disposables) d.dispose();
  return target.texture;
}
